import React from 'react';
import PropTypes from 'prop-types';
import { Link } from 'react-router-dom';

import Flex from '@decisiv/ui-components/lib/components/Flex';
import Table from '@decisiv/ui-components/lib/components/Table';
import Tag from '@decisiv/ui-components/lib/components/Tag';
import { P } from '@decisiv/ui-components/lib/components/Typography';
import { t, Trans } from '@lingui/macro';

// Counts, not per-peer outcomes, so the wording differs from the single-location
// cascade summary: "30 created" reads as work done, where one peer's `created`
// reads as the state it ended in.
const NEUTRAL = 'licoriceMousse';

// Count-neutral wording on purpose: the app has no plural rules loaded, and
// "1 need data" reads wrong while "Needs data: 1" is right at any count.
const countLabel = (status, count) =>
  ({
    created: { text: t`Created: ${count}`, color: 'success' },
    enabled: { text: t`Enabled: ${count}`, color: 'success' },
    disabled: { text: t`Disabled: ${count}`, color: NEUTRAL },
    removed: { text: t`Removed: ${count}`, color: NEUTRAL },
    blocked: { text: t`Needs data: ${count}`, color: 'warning' },
    failed: { text: t`Failed: ${count}`, color: 'danger' },
    excluded: { text: t`Excluded from sync: ${count}`, color: NEUTRAL },
    not_present: { text: t`Not configured: ${count}`, color: NEUTRAL },
    already_present: { text: t`Already there: ${count}`, color: NEUTRAL },
    already_enabled: { text: t`Already enabled: ${count}`, color: NEUTRAL },
    already_disabled: { text: t`Already disabled: ${count}`, color: NEUTRAL },
    // A status this build does not know about still goes through i18n, so its
    // shape is translatable even though the word itself comes from the API.
  }[status] ?? { text: t`${status}: ${count}`, color: NEUTRAL });

export default function BulkSyncSummary({ summary }) {
  const { shared_setting: sharedSetting, rows, integrations } = summary;

  const columns = [
    {
      DataCell: (data) => <span>{data.rowData.name}</span>,
      title: t`Integration`,
      name: 'integration',
    },
    {
      DataCell: (data) => (
        <Flex flexWrap="wrap" style={{ gap: '0.25rem' }}>
          {data.rowData.outcomes.map((outcome) => (
            <Tag key={outcome.text} text={outcome.text} color={outcome.color} />
          ))}
        </Flex>
      ),
      title: t`Outcome`,
      name: 'outcome',
    },
  ];

  const tableRows = integrations.map((integration) => ({
    id: integration.integration_uuid,
    name: integration.integration_name,
    outcomes: Object.entries(integration.statuses).map(([status, count]) =>
      countLabel(status, count),
    ),
  }));

  const skippedLabel = t`Rows that changed nothing to propagate: ${rows.skipped}`;

  return (
    <Flex flexDirection="column" marginTop={1} data-testid="bulk-sync-summary">
      {rows.skipped > 0 && (
        <P size="small" shade={1} marginBottom={0.5}>
          {skippedLabel}
        </P>
      )}

      {tableRows.length > 0 && (
        <Table
          columns={columns}
          data={tableRows}
          kind="secondary"
          getRowKey={({ id }) => id}
        />
      )}

      {sharedSetting && rows.needs_attention > 0 && (
        <P size="small" marginTop={0.5}>
          <Link
            to={`/toolkit/shared_settings/${sharedSetting.uuid}/needs_attention`}
          >
            <Trans>Review the locations that need attention</Trans>
          </Link>
        </P>
      )}
    </Flex>
  );
}

BulkSyncSummary.propTypes = {
  summary: PropTypes.shape({
    shared_setting: PropTypes.shape({
      uuid: PropTypes.string,
      name: PropTypes.string,
    }),
    rows: PropTypes.shape({
      cascaded: PropTypes.number,
      fully_synced: PropTypes.number,
      needs_attention: PropTypes.number,
      failed: PropTypes.number,
      skipped: PropTypes.number,
    }).isRequired,
    integrations: PropTypes.arrayOf(
      PropTypes.shape({
        integration_uuid: PropTypes.string,
        integration_name: PropTypes.string,
        statuses: PropTypes.objectOf(PropTypes.number),
      }),
    ).isRequired,
  }).isRequired,
};
